import { type FC, useState } from 'react';
import { Card } from '../Card';
import { Button } from '../Button';
import styled, { useTheme } from 'styled-components';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { useManagerTeamCampaignsQuery, type ManagerTeamCampaign } from '../../apiHooks/useManagerTeamCampaignsQuery';
import { Icon, IconType } from '../Props/Icon/Icon';
import { useLanguageContext } from '../../lib/hooks/useLanguageContext';
import { TrainingCompletionModal } from './components/TrainingCompletionModal';
import { NudgeModal, type NudgeModalConfigs } from '../Nudge/NudgeModal';
import { NudgeType } from '../Nudge/enums/NudgeType';
import { useCampaignSendNudgeMutation } from '../../apiHooks/useCampaignSendNudgeMutation';
import toast from 'react-hot-toast';
import { useNamedContentEntryText } from '../../lib/hooks/contentEntry/useNamedContentEntryText';

export const ManagerTeamTrainingWidget: FC = () => {
  const { data: managerListTrainingCampaigns } = useManagerTeamCampaignsQuery();
  const [langCode] = useLanguageContext();
  const theme = useTheme();

  const { mandatoryCampaigns, completedCampaigns } = (managerListTrainingCampaigns || []).reduce(
    (
      acc: { mandatoryCampaigns: ManagerTeamCampaign[]; completedCampaigns: ManagerTeamCampaign[] },
      campaign: ManagerTeamCampaign,
    ) => {
      if (campaign.campaignStatus === 'completed') {
        acc.completedCampaigns.push(campaign);
      } else {
        acc.mandatoryCampaigns.push(campaign);
      }
      return acc;
    },
    { mandatoryCampaigns: [], completedCampaigns: [] },
  );

  const [isCompletedSectionOpen, setIsCompletedSectionOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<ManagerTeamCampaign | null>(null);
  const [nudgeCampaignId, setNudgeCampaignId] = useState<string | null>(null);
  const [nudgeModalConfigs, setNudgeModalConfigs] = useState<NudgeModalConfigs>({
    isOpen: false,
    type: NudgeType.REMINDER,
  });
  const successToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeSentSuccessfully',
  });
  const failToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeFailure',
  });
  const { mutate, isPending } = useCampaignSendNudgeMutation();

  const handleSendNudge = () => {
    if (!nudgeCampaignId) {
      return;
    }

    mutate(
      {
        campaignId: nudgeCampaignId,
      },
      {
        onSuccess: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon icon={IconType.CHECK_CIRCLE} width={20} height={20} fill="transparent" />
                <span>{successToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: theme.colors.heading,
                color: theme.colors.primaryContrast,
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
            type: NudgeType.REMINDER,
          });
          setNudgeCampaignId(null);
        },
        onError: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon
                  className="alert-triangle"
                  icon={IconType.ALERT_TRIANGLE}
                  width={20}
                  height={20}
                  fill="transparent"
                />
                <span>{failToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: theme.colors.heading,
                color: theme.colors.primaryContrast,
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
            type: NudgeType.REMINDER,
          });
        },
      },
    );
  };

  return (
    <>
      <Card withShadow withBorder gap={20}>
        <WidgetHeader>
          <HeaderMain>
            <HeaderIcon>
              <Icon icon={IconType.GRADUATE_CAP} width={16} height={16} />
            </HeaderIcon>
            <HeaderText>
              <HeaderTitle>
                <NamedContentEntryText container="span" refKey="managerSectionTitles" subKey="cybersecurityTraining" />
              </HeaderTitle>
            </HeaderText>
          </HeaderMain>
          <ActiveBadge>{mandatoryCampaigns.length} active</ActiveBadge>
        </WidgetHeader>

        <SectionContent>
          {mandatoryCampaigns.map((campaign: ManagerTeamCampaign) => {
            const dueDate = calculateDueDate(campaign.startDate, campaign.durationDays);
            const isOverdue = new Date(dueDate).getTime() < Date.now();
            const progress = campaign.enrolled > 0 ? (campaign.completed / campaign.enrolled) * 100 : 0;

            return (
              <CampaignCard key={campaign.campaignId}>
                <CampaignHeader>
                  <CampaignTitle>{campaign.campaignName}</CampaignTitle>
                  <CampaignDate $isOverdue={isOverdue}>
                    <Icon icon={IconType.CALENDAR} width={12} height={12} fill="none" />
                    <span>{formatDueDate(dueDate, langCode)}</span>
                  </CampaignDate>
                </CampaignHeader>

                <ProgressTrack>
                  <ProgressValue $progress={progress} $isOverdue={isOverdue} />
                </ProgressTrack>

                <CampaignFooter>
                  <CampaignMetaButton
                    type="button"
                    onClick={() => setSelectedCampaign(campaign)}
                    aria-label={`View completion details for ${campaign.campaignName}`}
                  >
                    <Icon icon={IconType.USER_COUNT} width={12} height={12} fill="none" />
                    <span>
                      {campaign.completed || 0} / {campaign.enrolled || 0}
                    </span>
                  </CampaignMetaButton>

                  <CampaignMeta>
                    <Icon icon={IconType.CLOCK} width={12} height={12} fill="none" />
                    <span>{(campaign.mandatoryModules.length + campaign.dynamicModules.length) * 5} min</span>
                  </CampaignMeta>

                  <NudgeButton
                    type="button"
                    onClick={() => {
                      setNudgeCampaignId(campaign.campaignId);
                      setNudgeModalConfigs({
                        isOpen: true,
                        type: NudgeType.REMINDER,
                      });
                    }}
                  >
                    <NamedContentEntryText container="span" refKey="managerTeamCyberSecurityTraining" subKey="nudge" />
                  </NudgeButton>
                </CampaignFooter>
              </CampaignCard>
            );
          })}

          <CompletedSection>
            <CompletedToggle
              type="button"
              onClick={() => setIsCompletedSectionOpen((prev) => !prev)}
              aria-expanded={isCompletedSectionOpen}
            >
              <CompletedLabel>
                <ChevronIcon $isOpen={isCompletedSectionOpen}>
                  <Icon icon={IconType.CHEVRON_DOWN} width={14} height={14} fill="none" />
                </ChevronIcon>
                <span>
                  <NamedContentEntryText
                    container="span"
                    refKey="managerTeamCyberSecurityTraining"
                    subKey="completed"
                  />{' '}
                  ({completedCampaigns.length})
                </span>
              </CompletedLabel>
            </CompletedToggle>

            {isCompletedSectionOpen &&
              completedCampaigns.map((campaign: ManagerTeamCampaign) => (
                <CompletedCampaignRow key={campaign.campaignId}>
                  <CompletedCampaignName>
                    <CompletedCheckIcon viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <path d="m9 12 2 2 4-4" />
                    </CompletedCheckIcon>
                    <span>{campaign.campaignName}</span>
                  </CompletedCampaignName>
                  <RevisitButton
                    type="button"
                    disabled={!campaign.campaignUrl}
                    onClick={() => {
                      if (campaign.campaignUrl) {
                        window.open(campaign.campaignUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <NamedContentEntryText container="span" refKey="yourTraining" subKey="revisit" />
                  </RevisitButton>
                </CompletedCampaignRow>
              ))}
          </CompletedSection>
        </SectionContent>
      </Card>

      {selectedCampaign && (
        <TrainingCompletionModal
          campaignId={selectedCampaign.campaignId}
          isOpen={Boolean(selectedCampaign)}
          onClose={() => setSelectedCampaign(null)}
          statsCard={{
            title: selectedCampaign.campaignName,
            dueDate: calculateDueDate(selectedCampaign.startDate, selectedCampaign.durationDays),
            totalLearners: selectedCampaign.enrolled || 0,
            learnersCompletedTotal: selectedCampaign.completed || 0,
            estimatedCompletionTime:
              (selectedCampaign.mandatoryModules.length + selectedCampaign.dynamicModules.length) * 5,
          }}
        />
      )}

      <NudgeModal
        nudgeConfigs={nudgeModalConfigs}
        onClose={() => {
          setNudgeModalConfigs({
            isOpen: false,
            type: NudgeType.REMINDER,
          });
          setNudgeCampaignId(null);
        }}
        onSendNudge={handleSendNudge}
        isSending={isPending}
      />
    </>
  );
};

const calculateDueDate = (startDate: string, durationDays: number): string => {
  const start = new Date(startDate);
  start.setDate(start.getDate() + durationDays);
  return start.toISOString();
};

const formatDueDate = (dueDate: string, langCode?: string) =>
  new Date(dueDate).toLocaleString(langCode, { month: 'short', day: 'numeric' });

const WidgetHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(54, 94, 206, 0.1);
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const HeaderTitle = styled.div`
  color: ${({ theme }) => theme.colors.heading};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
`;

const ActiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(54, 94, 206, 0.1);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 10px;
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CampaignCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.topBarBorder};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.panelBackground};
`;

const CampaignHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const CampaignTitle = styled.div`
  color: ${({ theme }) => theme.colors.heading};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
`;

const CampaignDate = styled.div<{ $isOverdue: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme, $isOverdue }) => ($isOverdue ? theme.colors.statusDanger : theme.colors.normal)};
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.pageBackground};
  overflow: hidden;
`;

const ProgressValue = styled.div<{ $progress: number; $isOverdue: boolean }>`
  width: ${({ $progress }) => `${$progress}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme, $isOverdue }) => ($isOverdue ? theme.colors.statusDanger : theme.colors.primary)};
`;

const CampaignFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const CampaignMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.normal};
  font-size: 11px;
`;

const CampaignMetaButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.normal};
  font-size: 11px;
  cursor: pointer;
`;

const NudgeButton = styled(Button).attrs({
  size: 'small',
  backgroundColor: '#DDF5E8',
  color: '#1F9A67',
  padding: '2px 10px',
  borderRadius: '999px',
})`
  margin-left: auto;
  font-weight: 500;
  font-size: 11px;
  &:hover {
    filter: brightness(0.98);
  }
`;

const CompletedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CompletedToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const CompletedLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.normal};
  font-size: 12px;
  font-weight: 500;
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${({ $isOpen, theme }) => ($isOpen ? 0 : theme.rtl ? 90 : -90)}deg);
  transition: transform 0.2s ease-in-out;
`;

const CompletedCampaignRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 2px;
`;

const CompletedCampaignName = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.normal};
  font-size: 12px;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    color: ${({ theme }) => theme.colors.success};
    flex-shrink: 0;
  }

  svg {
    flex-shrink: 0;
  }
`;

const CompletedCheckIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.success};
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const RevisitButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.colors.mutedForeground};
    cursor: default;
  }
`;

const NudgeToast = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .alert-triangle {
    color: ${({ theme }) => theme.colors.warningBackground};
  }
`;
